import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  IconButton,
  Link,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { PodcastSpotAPI } from 'api/podcastSpotAPI';
import dayjs from 'dayjs';

import { OutlinedButton, SubmitButton } from 'components/UIComponents';
import { StyledDialog as Dialog } from 'components/base/BaseModal/styledDialog';
import { StyledDialogActions as DialogActions } from 'components/base/BaseModal/styledDialogActions';
import { StyledDialogTitle as DialogTitle } from 'components/base/BaseModal/styledDialogTitle';
import {
  StyledBodyTableCell as BodyTableCell,
  StyledHeadTableCell as HeadTableCell,
  StyledTableHead as TableHead,
  StyledTableRow as BodyTableRow,
} from 'components/base/TableWithLazyLoad/styledComponents';

import { useToast } from 'context/ToastContext';

import { ToastType } from 'constants/enums/PopupType';

import { ReactComponent as CheckboxChecked } from 'assets/icons/CheckboxChecked.svg';
import { ReactComponent as CheckboxUnchecked } from 'assets/icons/CheckboxUnchecked.svg';

import { IOriginSpot } from 'interfaces/spots/Podcast/IPodcastForm';

import { colors } from 'styles/globalStyles';

interface ApiCandidate {
  id: number;
  outlet_id: number;
  outlet_name: string;
  product_id: number;
  product_name: string;
  start_date: string;
  total_gross: string;
}

const HTTP_STATUS_BAD_REQUEST = 400;

interface OriginSpotPickerProps {
  outletId: number;
  productId: number;
  excludeId?: number;
  initiallySelected: number[];
  initiallySelectedSpots?: IOriginSpot[];
  onCancel: () => void;
  onSave: (selected: IOriginSpot[]) => void;
}

const apiToOriginSpot = (c: ApiCandidate): IOriginSpot => ({
  id: c.id,
  outletId: c.outlet_id,
  outletName: c.outlet_name,
  productId: c.product_id,
  productName: c.product_name,
  startDate: c.start_date,
  totalGross: c.total_gross,
});

const formatGross = (v: string): string => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const OriginSpotPicker = ({
  outletId,
  productId,
  excludeId,
  initiallySelected,
  initiallySelectedSpots,
  onCancel,
  onSave,
}: OriginSpotPickerProps) => {
  const [candidates, setCandidates] = useState<IOriginSpot[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(initiallySelected),
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { onToastOpen } = useToast();

  const initiallySelectedSpotsRef = useRef<IOriginSpot[]>(initiallySelectedSpots ?? []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    PodcastSpotAPI.getOriginCandidates(outletId, productId, excludeId)
      .then((response: { status?: number; data?: { data?: ApiCandidate[] } } | undefined) => {
        if (cancelled) return;
        const ok = !!response && (response.status === undefined || response.status < HTTP_STATUS_BAD_REQUEST);
        if (!ok) {
          setLoadError(true);
          setCandidates([]);
          onToastOpen(
            'ORIGIN_CANDIDATES_LOAD_ERROR',
            'Failed to load candidate spots',
            ToastType.error,
          );
          return;
        }
        const data: ApiCandidate[] = response?.data?.data ?? [];
        const apiCandidates = data.map(apiToOriginSpot);
        const apiIds = new Set(apiCandidates.map((c) => c.id));
        const carryOver = initiallySelectedSpotsRef.current.filter((s) => !apiIds.has(s.id));
        setCandidates([...carryOver, ...apiCandidates]);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
        setCandidates([]);
        onToastOpen(
          'ORIGIN_CANDIDATES_LOAD_ERROR',
          'Failed to load candidate spots',
          ToastType.error,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [outletId, productId, excludeId, onToastOpen]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selected = useMemo(
    () => candidates.filter((c) => selectedIds.has(c.id)),
    [candidates, selectedIds],
  );

  const showTable = !loading && !loadError && candidates.length > 0;

  return (
    <Dialog open onClose={onCancel} maxWidth="md">
      <DialogTitle>
        Spot selection
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{ color: colors.white, padding: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading && (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">Loading...</Typography>
        </Box>
      )}

      {!loading && loadError && (
        <Box p={4} textAlign="center">
          <Typography color="error">
            Failed to load candidate spots. Please try again.
          </Typography>
        </Box>
      )}

      {!loading && !loadError && candidates.length === 0 && (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            No matching spots in the last 6 months.
          </Typography>
        </Box>
      )}

      {showTable && (
        <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <HeadTableCell sx={{ width: 48, paddingLeft: '30px' }} />
                <HeadTableCell>Outlet</HeadTableCell>
                <HeadTableCell>Date</HeadTableCell>
                <HeadTableCell>Total Gross</HeadTableCell>
                <HeadTableCell sx={{ paddingRight: '30px' }}>Product</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((c) => (
                <BodyTableRow key={c.id}>
                  <BodyTableCell sx={{ width: 48, paddingLeft: '30px' }}>
                    <Checkbox
                      checked={selectedIds.has(c.id)}
                      checkedIcon={<CheckboxChecked color={colors.red} />}
                      icon={<CheckboxUnchecked />}
                      onChange={() => toggle(c.id)}
                      sx={{ padding: 0 }}
                      inputProps={{ 'aria-label': `Select ${c.outletName} ${c.startDate}` }}
                    />
                  </BodyTableCell>
                  <BodyTableCell>
                    <Link
                      component={RouterLink}
                      to={`/outlets/${c.outletId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      variant="inherit"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        fontSize: '14px',
                        letterSpacing: '-0.32px',
                        color: colors.darkGrey,
                        textDecorationColor: colors.darkGrey,
                        '&:hover': {
                          color: colors.darkGrey,
                          textDecorationColor: colors.darkGrey,
                        },
                      }}
                    >
                      {c.outletName}
                    </Link>
                  </BodyTableCell>
                  <BodyTableCell>{dayjs(c.startDate).format('MM/DD/YYYY')}</BodyTableCell>
                  <BodyTableCell>{formatGross(c.totalGross)}</BodyTableCell>
                  <BodyTableCell sx={{ paddingRight: '30px' }}>{c.productName}</BodyTableCell>
                </BodyTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DialogActions>
        <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
        <SubmitButton
          onClick={() => onSave(selected)}
          disabled={selected.length === 0}
        >
          Save
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};

export default OriginSpotPicker;
